import React, { useState, useEffect, useRef } from 'react';
import { PlusIcon, PencilIcon, QrCodeIcon, PrinterIcon, ArrowDownTrayIcon, TrashIcon } from '@heroicons/react/24/outline';
import { QRTable, TableSession, ServerOrder } from '../types';
import { apiService } from '../services/api';
import QRCode from 'qrcode';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { buildRestaurantTableUrl } from '../utils/restaurantUtils';

interface QRTableListProps {
  onShowSubscriptionModal?: () => void;
}

type SmallQRPrintLabel = {
  tableName: string;
  qrDataURL: string;
};

const DEFAULT_QR_SCAN_TEXT = 'SCAN TO ORDER';
const QR_PRINT_SCAN_TEXT_STORAGE_KEY = 'menunix_qr_print_scan_text';

type QRPrintOptionsMode = 'compact-single' | 'compact-bulk' | 'menu-single' | 'menu-bulk';

/** Menu / stand card: 6×4 in (10.16 × 15.24 cm), 2 per A4 row */
const MENU_QR_CARD_LAYOUT = {
  cardWidthMm: 101.6,
  cardHeightMm: 152.4,
  gridCols: 2,
  gridRows: 1,
  itemsPerPage: 2,
  pagePaddingMm: 4,
  qrBoxPx: 180,
} as const;

/** Small QR label: ~3 × 2.75 in, 8 per A4 sheet */
const SMALL_QR_LABEL_LAYOUT = {
  stickerWidthMm: 76.2,
  stickerHeightMm: 69.85,
  qrBoxMm: 48,
  scanFontPx: 10,
  tableNameFontPx: 10,
  gridCols: 2,
  gridRows: 4,
  labelsPerPage: 8,
  pagePaddingMm: 8,
} as const;

const escapePrintHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const readStoredQRScanText = (): string => {
  try {
    const saved = localStorage.getItem(QR_PRINT_SCAN_TEXT_STORAGE_KEY);
    if (saved?.trim()) return saved.trim();
  } catch {
    /* ignore */
  }
  return DEFAULT_QR_SCAN_TEXT;
};

const normalizeScanPrintText = (value: string): string => {
  const trimmed = value.trim();
  return trimmed ? trimmed : DEFAULT_QR_SCAN_TEXT;
};

const QR_CODE_GENERATION_OPTIONS = {
  width: 300,
  margin: 2,
  color: {
    dark: '#000000',
    light: '#FFFFFF',
  },
} as const;

const isMobilePrintDevice = () =>
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

/** Opens print dialog for QR HTML (shared by small-label print only). */
const openQRPrintDocument = (printHTML: string, onComplete?: () => void) => {
  const finish = () => onComplete?.();

  if (isMobilePrintDevice()) {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!iframeDoc) {
      finish();
      return;
    }

    iframeDoc.open();
    iframeDoc.write(printHTML);
    iframeDoc.close();

    const cleanup = () => {
      if (iframe.parentNode) {
        document.body.removeChild(iframe);
      }
      finish();
    };

    const triggerPrint = () => {
      if (iframe.contentWindow) {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      }
      setTimeout(cleanup, 1000);
    };

    iframe.onload = () => setTimeout(triggerPrint, 500);
    setTimeout(triggerPrint, 1000);
    return;
  }

  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    finish();
    return;
  }

  printWindow.document.write(printHTML);
  printWindow.document.close();

  let hasPrinted = false;
  const triggerPrint = () => {
    if (hasPrinted || printWindow.closed) return;
    hasPrinted = true;
    setTimeout(() => {
      if (printWindow.closed) {
        finish();
        return;
      }
      printWindow.print();
      printWindow.onafterprint = () => {
        if (!printWindow.closed) printWindow.close();
        finish();
      };
      setTimeout(() => {
        if (!printWindow.closed) printWindow.close();
        finish();
      }, 1000);
    }, 500);
  };

  printWindow.onload = () => {
    const images = printWindow.document.querySelectorAll('img');
    if (images.length === 0) {
      triggerPrint();
      return;
    }
    let imagesLoaded = 0;
    const checkAllLoaded = () => {
      imagesLoaded += 1;
      if (imagesLoaded === images.length) triggerPrint();
    };
    images.forEach((img) => {
      if (img.complete) checkAllLoaded();
      else {
        img.onload = checkAllLoaded;
        img.onerror = checkAllLoaded;
      }
    });
    setTimeout(() => {
      if (!hasPrinted && !printWindow.closed) triggerPrint();
    }, 3000);
  };
};

const buildCompactQRCodesPrintHTML = (
  labels: SmallQRPrintLabel[],
  options: {
    title: string;
    textSecondaryColor?: string;
    scanText?: string;
  }
) => {
  const layout = SMALL_QR_LABEL_LAYOUT;
  const textSecondaryColor = options.textSecondaryColor || '#4b5563';
  const scanText = escapePrintHtml(normalizeScanPrintText(options.scanText || DEFAULT_QR_SCAN_TEXT));
  const labelsPerPage = layout.labelsPerPage;

  const pages: SmallQRPrintLabel[][] = [];
  for (let i = 0; i < labels.length; i += labelsPerPage) {
    pages.push(labels.slice(i, i + labelsPerPage));
  }

  const renderLabel = (label: SmallQRPrintLabel) => {
    const tableName = escapePrintHtml(label.tableName);
    return `
    <div class="compact-sticker">
      <div class="qr-code">
        <img src="${label.qrDataURL}" alt="QR Code for Table ${tableName}" />
        <div class="table-name">${tableName}</div>
      </div>
      <div class="scan-text">${scanText}</div>
    </div>
  `;
  };

  const pageGridStyle = `
            display: grid;
            grid-template-columns: repeat(${layout.gridCols}, ${layout.stickerWidthMm}mm);
            grid-template-rows: repeat(${layout.gridRows}, ${layout.stickerHeightMm}mm);
            gap: 4mm;
            justify-content: center;
            align-content: center;
          `;

  const pagesHTML = pages
    .map(
      (pageLabels) => `
      <div class="page">
        ${pageLabels.map(renderLabel).join('')}
      </div>
    `
    )
    .join('');

  return `
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="UTF-8">
        <title>${escapePrintHtml(options.title)}</title>
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600&display=swap" rel="stylesheet">
        <style>
          @page { size: A4; margin: 0; }
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body {
            font-family: 'Inter', sans-serif;
            margin: 0;
            padding: 0;
            background: #ffffff;
          }
          .page {
            width: 210mm;
            height: 297mm;
            padding: ${layout.pagePaddingMm}mm;
            ${pageGridStyle}
            page-break-after: always;
            break-after: page;
          }
          .page:last-child {
            page-break-after: auto;
            break-after: auto;
          }
          .compact-sticker {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
            padding: 2mm;
            gap: 2mm;
            width: ${layout.stickerWidthMm}mm;
            height: ${layout.stickerHeightMm}mm;
            margin: 0 auto;
          }
          .scan-text {
            font-size: ${layout.scanFontPx}px;
            font-weight: 500;
            letter-spacing: 0.2px;
            line-height: 1.3;
            text-align: center;
            max-width: 100%;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            color: ${textSecondaryColor};
            margin-top: 2mm;
            text-transform: uppercase;
          }
          .qr-code {
            position: relative;
            width: ${layout.qrBoxMm}mm;
            height: ${layout.qrBoxMm}mm;
            max-width: ${layout.qrBoxMm}mm;
            max-height: ${layout.qrBoxMm}mm;
            flex-shrink: 0;
            padding: 12px;
            background: #ffffff;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.08);
            box-sizing: border-box;
          }
          .qr-code img {
            width: 100%;
            height: 100%;
            display: block;
            border-radius: 4px;
            object-fit: contain;
          }
          .table-name {
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
            z-index: 1;
            max-width: 72%;
            padding: 2px 6px;
            font-size: ${layout.tableNameFontPx}px;
            font-weight: 500;
            letter-spacing: 0.2px;
            line-height: 1.3;
            text-align: center;
            color: ${textSecondaryColor};
            background: #ffffff;
            border-radius: 4px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            box-shadow: 0 0 0 2px #ffffff;
          }
          @media print {
            @page { size: A4; margin: 0; }
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              background: #ffffff !important;
            }
            .page {
              width: 210mm !important;
              height: 297mm !important;
              page-break-after: always !important;
              break-after: page !important;
            }
            .page:last-child {
              page-break-after: auto !important;
              break-after: auto !important;
            }
            .qr-code {
              background: #ffffff !important;
              box-shadow: 0 2px 8px rgba(0,0,0,0.08) !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .qr-code img {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .scan-text {
              color: ${textSecondaryColor} !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .table-name {
              color: ${textSecondaryColor} !important;
              background: #ffffff !important;
              box-shadow: 0 0 0 2px #ffffff !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
          }
          @media screen {
            body { background: #f0f0f0; padding: 20px; }
            .page { margin-bottom: 20px; background: #ffffff; }
          }
        </style>
      </head>
      <body>
        ${pagesHTML}
      </body>
    </html>
  `;
};

const generateTableQRDataURL = (tableUrl: string) =>
  QRCode.toDataURL(tableUrl, QR_CODE_GENERATION_OPTIONS);

const QRTableList: React.FC<QRTableListProps> = ({ onShowSubscriptionModal }) => {
  const { hasProPlan } = useAuth();
  const { showSuccess, showError } = useToast();
  const [tables, setTables] = useState<QRTable[]>([]);
  const [loading, setLoading] = useState(true);
  // Removed local error banner; using toasts instead
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [newTableName, setNewTableName] = useState('');
  const [editingTable, setEditingTable] = useState<QRTable | null>(null);
  const [editTableName, setEditTableName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isRemoveSessionModalOpen, setIsRemoveSessionModalOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState<TableSession | null>(null);
  const [isRemovingSession, setIsRemovingSession] = useState(false);
  const [isQRModalOpen, setIsQRModalOpen] = useState(false);
  const [selectedTableForQR, setSelectedTableForQR] = useState<QRTable | null>(null);
  const [qrCodeDataURL, setQrCodeDataURL] = useState<string>('');
  const [isGeneratingQR, setIsGeneratingQR] = useState(false);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<string>('');
  const [customerOrders, setCustomerOrders] = useState<ServerOrder[]>([]);
  const [customerOrdersLoading, setCustomerOrdersLoading] = useState(false);
  const [selectedTableIds, setSelectedTableIds] = useState<Set<string>>(new Set());
  const [isPrintingMultiple, setIsPrintingMultiple] = useState(false);
  const [isPrintingSmallMultiple, setIsPrintingSmallMultiple] = useState(false);
  const [isQRPrintOptionsOpen, setIsQRPrintOptionsOpen] = useState(false);
  const [qrPrintOptionsMode, setQrPrintOptionsMode] = useState<QRPrintOptionsMode>('compact-single');
  const [qrPrintScanTextDraft, setQrPrintScanTextDraft] = useState(() => readStoredQRScanText());

  // Ensure page starts at top when visiting this screen
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  // Handle browser back button to close QR Code Modal
  useEffect(() => {
    if (!isQRModalOpen) return;

    const handlePopState = () => {
      setIsQRModalOpen(false);
    };

    // Push history state when modal opens
    window.history.pushState({ modalOpen: true }, '');

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isQRModalOpen]);

  // Handle browser back button to close Customer Orders Modal
  useEffect(() => {
    if (!isCustomerModalOpen) return;

    const handlePopState = () => {
      setIsCustomerModalOpen(false);
    };

    // Push history state when modal opens
    window.history.pushState({ modalOpen: true }, '');

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isCustomerModalOpen]);

  // Handle browser back button to close Create Table Modal
  useEffect(() => {
    if (!isCreateModalOpen) return;

    const handlePopState = () => {
      setIsCreateModalOpen(false);
    };

    // Push history state when modal opens
    window.history.pushState({ modalOpen: true }, '');

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isCreateModalOpen]);

  // Handle browser back button to close Edit Table Modal
  useEffect(() => {
    if (!isEditModalOpen) return;

    const handlePopState = () => {
      setIsEditModalOpen(false);
    };

    // Push history state when modal opens
    window.history.pushState({ modalOpen: true }, '');

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isEditModalOpen]);


  const fetchTableSessions = async () => {
    setLoading(true);
    try {
      const response: any = await apiService.getTableSessions();
      if (response && response.status) {
        setTables(response.data || []);
      } else {
        showError('Tables', response?.message || 'Failed to fetch table sessions');
      }
    } catch (e) {
      showError('Tables', 'An error occurred while fetching table sessions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTableSessions();
  }, []);

  // Check if user has pro plan - moved after hooks
  // if (!hasProPlan()) {
  //   return (
  //     <div className="flex items-center justify-center min-h-[400px]">
  //       <div className="text-center max-w-md mx-auto">
  //         <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-6">
  //           <LockClosedIcon className="w-8 h-8 text-amber-600" />
  //         </div>
  //         <h3 className="text-lg font-semibold text-gray-900 mb-2">Pro Feature</h3>
  //         <p className="text-gray-600 mb-6">
  //           QR table management is available with a Pro subscription. 
  //           Upgrade now to manage tables, track customer sessions, and generate QR codes.
  //         </p>
  //         <button
  //           onClick={() => onShowSubscriptionModal?.()}
  //           className="bg-orange-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-pink-600 focus:ring-offset-2 transition-colors"
  //         >
  //           Upgrade to Pro
  //         </button>
  //       </div>
  //     </div>
  //   );
  // }

  const formatDate = (epochMillis: number) => {
    return new Date(epochMillis).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleCustomerClick = async (customerId: string) => {
    setSelectedCustomer(customerId);
    window.history.pushState({ modalOpen: true }, '');
    setIsCustomerModalOpen(true);
    setCustomerOrdersLoading(true);
    
    try {
      const response = await apiService.getOrdersByCustomer(customerId);
      if (response && response.status) {
        setCustomerOrders(response.data || []);
      } else {
        showError('Orders', response?.message || 'Failed to fetch customer orders');
        setCustomerOrders([]);
      }
    } catch (error) {
      console.error('Error fetching customer orders:', error);
      showError('Orders', 'Failed to fetch customer orders');
      setCustomerOrders([]);
    } finally {
      setCustomerOrdersLoading(false);
    }
  };

  const calculateTotalAmount = (orders: ServerOrder[]) => {
    return orders.reduce((total, order) => total + order.totalAmount, 0);
  };

  const getRestaurantDetails = () => {
    try {
      const restaurantData = localStorage.getItem('restaurantDetails');
      if (restaurantData) {
        return JSON.parse(restaurantData);
      }
    } catch (error) {
      console.error('Error parsing restaurant details from localStorage:', error);
    }
    return null;
  };

  const handlePrint = () => {
    const printContent = document.getElementById('customer-orders-print');
    if (printContent) {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        const restaurantDetails = getRestaurantDetails();
        const totalAmount = calculateTotalAmount(customerOrders);
        
        const printHTML = `
          <html>
            <head>
              <title>&nbsp;</title>
              <style>
                body { 
                  font-family: Arial, sans-serif; 
                  margin: 0; 
                  padding: 10px; 
                  font-size: 12px;
                  line-height: 1.4;
                  color: #333;
                }
                .header { 
                  text-align: center; 
                  margin-bottom: 20px; 
                  padding-bottom: 10px;
                  border-bottom: 2px solid #333;
                }
                .restaurant-name { 
                  font-size: 20px; 
                  font-weight: bold; 
                  margin: 0 0 5px 0;
                  text-transform: uppercase;
                }
                .restaurant-details { 
                  color: #666; 
                  font-size: 11px; 
                  margin: 2px 0;
                }
                .customer-info { 
                  margin-top: 10px; 
                  padding-top: 8px; 
                  border-top: 1px solid #ddd;
                }
                .customer-name { 
                  font-size: 14px; 
                  font-weight: 600; 
                  margin: 0 0 3px 0;
                }
                .bill-details { 
                  color: #666; 
                  font-size: 11px; 
                  margin: 1px 0;
                }
                .order-item { 
                  border: 1px solid #ddd; 
                  margin-bottom: 10px; 
                  padding: 10px; 
                  border-radius: 5px;
                  background: #f9f9f9;
                }
                .order-header { 
                  font-weight: bold; 
                  margin-bottom: 8px; 
                  border-bottom: 1px solid #ccc; 
                  padding-bottom: 5px;
                  font-size: 13px;
                }
                .order-number { 
                  font-size: 14px; 
                  margin: 0 0 3px 0;
                }
                .order-meta { 
                  color: #666; 
                  font-size: 10px; 
                  margin: 1px 0;
                }
                .order-amount { 
                  font-size: 16px; 
                  font-weight: bold; 
                  color: #27ae60;
                }
                .status-badge { 
                  display: inline-block; 
                  padding: 2px 6px; 
                  border-radius: 10px; 
                  font-size: 9px; 
                  font-weight: bold;
                  text-transform: uppercase;
                }
                .items-section { 
                  background: white; 
                  padding: 8px; 
                  border-radius: 3px; 
                  margin: 8px 0;
                  border: 1px solid #e0e0e0;
                }
                .items-title { 
                  font-weight: 600; 
                  margin-bottom: 8px; 
                  border-bottom: 1px solid #ddd; 
                  padding-bottom: 3px;
                  font-size: 12px;
                }
                .product-item { 
                  margin: 5px 0; 
                  padding: 3px 0; 
                  border-bottom: 1px solid #f0f0f0;
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                }
                .product-name { 
                  font-weight: 500; 
                  font-size: 11px;
                  flex: 1;
                }
                .product-details { 
                  color: #666; 
                  font-size: 10px;
                  text-align: right;
                }
                .order-total { 
                  border-top: 1px solid #ccc; 
                  margin-top: 8px; 
                  padding-top: 8px; 
                  font-weight: bold;
                  background: #f0f0f0;
                  padding: 8px;
                  border-radius: 3px;
                  display: flex;
                  justify-content: space-between;
                }
                .grand-total { 
                  font-weight: bold; 
                  font-size: 16px; 
                  margin-top: 15px; 
                  text-align: right; 
                  border-top: 2px solid #333; 
                  padding-top: 10px;
                  background: #333;
                  color: white;
                  padding: 10px;
                  border-radius: 5px;
                }
                .no-orders { 
                  text-align: center; 
                  padding: 20px; 
                  color: #7f8c8d;
                  font-style: italic;
                }
                @media print { 
                  body { margin: 0; padding: 8px; font-size: 11px; }
                  .order-item { page-break-inside: avoid; margin-bottom: 8px; }
                  .grand-total { page-break-inside: avoid; }
                }
              </style>
            </head>
            <body>
              <div class="header">
                ${restaurantDetails ? `
                  <h1 class="restaurant-name">${restaurantDetails.name || 'Restaurant'}</h1>
                  ${restaurantDetails.address ? `<p class="restaurant-details">${restaurantDetails.address}</p>` : ''}
                  ${restaurantDetails.email ? `<p class="restaurant-details">Email: ${restaurantDetails.email}</p>` : ''}
                  ${restaurantDetails.description ? `<p class="restaurant-details" style="font-style: italic;">${restaurantDetails.description}</p>` : ''}
                  <div class="customer-info">
                    <h2 class="customer-name">Customer: ${selectedCustomer}</h2>
                    <p class="bill-details">Bill Date: ${new Date().toLocaleDateString('en-IN')}</p>
                    <p class="bill-details">Bill Time: ${new Date().toLocaleTimeString('en-IN')}</p>
                  </div>
                ` : `
                  <h2 class="customer-name">Customer: ${selectedCustomer}</h2>
                  <p class="bill-details">Bill Date: ${new Date().toLocaleDateString('en-IN')}</p>
                  <p class="bill-details">Bill Time: ${new Date().toLocaleTimeString('en-IN')}</p>
                `}
              </div>

              ${customerOrders.length === 0 ? `
                <div class="no-orders">No orders found for this customer.</div>
              ` : `
                ${customerOrders.map((order, index) => `
                  <div class="order-item">
                    <div class="order-header">
                      <div class="order-number">Order #${index + 1} - ${order.invoiceNo}</div>
                      <div class="order-meta">${formatDate(order.createdAt)}</div>
                      ${order.table ? `<div class="order-meta">Table: ${order.table}</div>` : ''}
                      ${order.status ? `<span class="status-badge" style="background: ${
                        order.status === 'New' ? '#fef3c7; color: #92400e' :
                        order.status === 'Confirmed' ? '#dbeafe; color: #1e40af' :
                        order.status === 'Preparing' ? '#e9d5ff; color: #7c3aed' :
                        order.status === 'Ready' ? '#ddd6fe; color: #5b21b6' :
                        order.status === 'Delivered' ? '#dcfce7; color: #166534' :
                        order.status === 'Cancelled' ? '#fecaca; color: #dc2626' :
                        '#f3f4f6; color: #374151'
                      }">${order.status}</span>` : ''}
                    </div>
                    
                    <div class="items-section">
                      <div class="items-title">Items Ordered:</div>
                      ${order.products.map((product) => `
                        <div class="product-item">
                          <div class="product-name">${product.name}</div>
                          <div class="product-details">
                            Qty: ${product.quantity} | ₹${product.offerPrice} | ₹${product.offerPrice * product.quantity}
                          </div>
                        </div>
                      `).join('')}
                      <div class="order-total">
                        <span>Order Total:</span>
                        <span class="order-amount">₹${order.totalAmount}</span>
                      </div>
                    </div>
                  </div>
                `).join('')}

                <div class="grand-total">
                  <span>Grand Total: ₹${totalAmount}</span>
                </div>
              `}
            </body>
          </html>
        `;
        
        printWindow.document.write(printHTML);
        printWindow.document.close();
        
        // Wait for the document to load, then print
        printWindow.onload = () => {
          printWindow.print();
          
          // Close the window after printing (with a small delay)
          setTimeout(() => {
            printWindow.close();
          }, 1000);
        };
        
        // Alternative: Close on focus (when user returns from print dialog)
        printWindow.onfocus = () => {
          setTimeout(() => {
            printWindow.close();
          }, 500);
        };
      }
    }
  };

  const handleDownload = () => {
    const printContent = document.getElementById('customer-orders-print');
    if (printContent) {
      const restaurantDetails = getRestaurantDetails();
      const totalAmount = calculateTotalAmount(customerOrders);
      const billHTML = `
        <html>
          <head>
            <title>&nbsp;</title>
            <style>
              body { 
                font-family: Arial, sans-serif; 
                margin: 0; 
                padding: 10px; 
                font-size: 12px;
                line-height: 1.4;
                color: #333;
              }
              .header { 
                text-align: center; 
                margin-bottom: 20px; 
                padding-bottom: 10px;
                border-bottom: 2px solid #333;
              }
              .restaurant-name { 
                font-size: 20px; 
                font-weight: bold; 
                margin: 0 0 5px 0;
                text-transform: uppercase;
              }
              .restaurant-details { 
                color: #666; 
                font-size: 11px; 
                margin: 2px 0;
              }
              .customer-info { 
                margin-top: 10px; 
                padding-top: 8px; 
                border-top: 1px solid #ddd;
              }
              .customer-name { 
                font-size: 14px; 
                font-weight: 600; 
                margin: 0 0 3px 0;
              }
              .bill-details { 
                color: #666; 
                font-size: 11px; 
                margin: 1px 0;
              }
              .order-item { 
                border: 1px solid #ddd; 
                margin-bottom: 10px; 
                padding: 10px; 
                border-radius: 5px;
                background: #f9f9f9;
              }
              .order-header { 
                font-weight: bold; 
                margin-bottom: 8px; 
                border-bottom: 1px solid #ccc; 
                padding-bottom: 5px;
                font-size: 13px;
              }
              .order-number { 
                font-size: 14px; 
                margin: 0 0 3px 0;
              }
              .order-meta { 
                color: #666; 
                font-size: 10px; 
                margin: 1px 0;
              }
              .order-amount { 
                font-size: 16px; 
                font-weight: bold; 
                color: #27ae60;
              }
              .status-badge { 
                display: inline-block; 
                padding: 2px 6px; 
                border-radius: 10px; 
                font-size: 9px; 
                font-weight: bold;
                text-transform: uppercase;
              }
              .items-section { 
                background: white; 
                padding: 8px; 
                border-radius: 3px; 
                margin: 8px 0;
                border: 1px solid #e0e0e0;
              }
              .items-title { 
                font-weight: 600; 
                margin-bottom: 8px; 
                border-bottom: 1px solid #ddd; 
                padding-bottom: 3px;
                font-size: 12px;
              }
              .product-item { 
                margin: 5px 0; 
                padding: 3px 0; 
                border-bottom: 1px solid #f0f0f0;
                display: flex;
                justify-content: space-between;
                align-items: center;
              }
              .product-name { 
                font-weight: 500; 
                font-size: 11px;
                flex: 1;
              }
              .product-details { 
                color: #666; 
                font-size: 10px;
                text-align: right;
              }
              .order-total { 
                border-top: 1px solid #ccc; 
                margin-top: 8px; 
                padding-top: 8px; 
                font-weight: bold;
                background: #f0f0f0;
                padding: 8px;
                border-radius: 3px;
                display: flex;
                justify-content: space-between;
              }
              .grand-total { 
                font-weight: bold; 
                font-size: 16px; 
                margin-top: 15px; 
                text-align: right; 
                border-top: 2px solid #333; 
                padding-top: 10px;
                background: #333;
                color: white;
                padding: 10px;
                border-radius: 5px;
              }
              .no-orders { 
                text-align: center; 
                padding: 20px; 
                color: #7f8c8d;
                font-style: italic;
              }
            </style>
          </head>
          <body>
            <div class="header">
              ${restaurantDetails ? `
                <h1 class="restaurant-name">${restaurantDetails.name || 'Restaurant'}</h1>
                ${restaurantDetails.address ? `<p class="restaurant-details">${restaurantDetails.address}</p>` : ''}
                ${restaurantDetails.email ? `<p class="restaurant-details">Email: ${restaurantDetails.email}</p>` : ''}
                ${restaurantDetails.description ? `<p class="restaurant-details" style="font-style: italic;">${restaurantDetails.description}</p>` : ''}
                <div class="customer-info">
                  <h2 class="customer-name">Customer: ${selectedCustomer}</h2>
                  <p class="bill-details">Bill Date: ${new Date().toLocaleDateString('en-IN')}</p>
                  <p class="bill-details">Bill Time: ${new Date().toLocaleTimeString('en-IN')}</p>
                </div>
              ` : `
                <h2 class="customer-name">Customer: ${selectedCustomer}</h2>
                <p class="bill-details">Bill Date: ${new Date().toLocaleDateString('en-IN')}</p>
                <p class="bill-details">Bill Time: ${new Date().toLocaleTimeString('en-IN')}</p>
              `}
            </div>

            ${customerOrders.length === 0 ? `
              <div class="no-orders">No orders found for this customer.</div>
            ` : `
              ${customerOrders.map((order, index) => `
                <div class="order-item">
                  <div class="order-header">
                    <div class="order-number">Order #${index + 1} - ${order.invoiceNo}</div>
                    <div class="order-meta">${formatDate(order.createdAt)}</div>
                    ${order.table ? `<div class="order-meta">Table: ${order.table}</div>` : ''}
                    ${order.status ? `<span class="status-badge" style="background: ${
                      order.status === 'New' ? '#fef3c7; color: #92400e' :
                      order.status === 'Confirmed' ? '#dbeafe; color: #1e40af' :
                      order.status === 'Preparing' ? '#e9d5ff; color: #7c3aed' :
                      order.status === 'Ready' ? '#ddd6fe; color: #5b21b6' :
                      order.status === 'Delivered' ? '#dcfce7; color: #166534' :
                      order.status === 'Cancelled' ? '#fecaca; color: #dc2626' :
                      '#f3f4f6; color: #374151'
                    }">${order.status}</span>` : ''}
                  </div>
                  
                  <div class="items-section">
                    <div class="items-title">Items Ordered:</div>
                    ${order.products.map((product) => `
                      <div class="product-item">
                        <div class="product-name">${product.name}</div>
                        <div class="product-details">
                          Qty: ${product.quantity} | ₹${product.offerPrice} | ₹${product.offerPrice * product.quantity}
                        </div>
                      </div>
                    `).join('')}
                    <div class="order-total">
                      <span>Order Total:</span>
                      <span class="order-amount">₹${order.totalAmount}</span>
                    </div>
                  </div>
                </div>
              `).join('')}

              <div class="grand-total">
                <span>Grand Total: ₹${totalAmount}</span>
              </div>
            `}
          </body>
        </html>
      `;

      const blob = new Blob([billHTML], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const safeCustomer = (selectedCustomer || 'customer').toString().replace(/[^a-z0-9_-]+/gi, '_');
      link.href = url;
      link.download = `Bill_${safeCustomer}_${new Date().toISOString().slice(0,10)}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const getSessionStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'completed':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };


  const handleCreateTable = async () => {
    if (!newTableName.trim()) {
      showError('Create Table', 'Table name is required');
      return;
    }

    setIsCreating(true);
    try {
      const response = await apiService.createTable(newTableName.trim());
      if (response && response.status) {
        // Clear localStorage to force fresh fetch on next load
        localStorage.removeItem('tables');
        // Refresh the table list
        await fetchTableSessions();
        setIsCreateModalOpen(false);
        setNewTableName('');
      } else {
        showError('Create Table', response?.message || 'Failed to create table');
      }
    } catch (err) {
      showError('Create Table', 'Failed to create table');
    } finally {
      setIsCreating(false);
    }
  };

  const handleEditTable = (table: QRTable) => {
    setEditingTable(table);
    setEditTableName(table.name);
    window.history.pushState({ modalOpen: true }, '');
    setIsEditModalOpen(true);
  };

  const handleUpdateTable = async () => {
    if (!editingTable || !editTableName.trim()) {
      showError('Update Table', 'Table name is required');
      return;
    }

    setIsUpdating(true);
    try {
      // Pass 'active' as default status since status field is removed from UI
      const response = await apiService.updateTable(editingTable._id, editTableName.trim(), 'active');
      if (response && response.status) {
        // Clear localStorage to force fresh fetch on next load
        localStorage.removeItem('tables');
        // Refresh the table list
        await fetchTableSessions();
        setIsEditModalOpen(false);
        setEditingTable(null);
        setEditTableName('');
      } else {
        showError('Update Table', response?.message || 'Failed to update table');
      }
    } catch (err) {
      showError('Update Table', 'Failed to update table');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async (table: QRTable) => {
    if (!window.confirm('Are you sure you want to delete this table?')) {
      return;
    }

    // If table doesn't have _id, reload from API first
    if (!table._id) {
      try {
        const reloadedTables = await apiService.getTableSessions();
        if (reloadedTables.status && reloadedTables.data) {
          setTables(reloadedTables.data);
          // Try to find the table by name
          const tableWithId = reloadedTables.data.find(
            (t: QRTable) => t.name === table.name
          );
          if (tableWithId && tableWithId._id) {
            // Now delete with the correct _id
            try {
              const response = await apiService.deleteTable(tableWithId._id);
              if (response.status) {
                // Clear localStorage to force fresh fetch on next load
                localStorage.removeItem('tables');
                setTables(reloadedTables.data.filter((t: QRTable) => t._id !== tableWithId._id));
                showSuccess('Table deleted successfully.');
              } else {
                showError('Failed to delete table', response.error || 'Unable to delete table.');
              }
            } catch (err) {
              showError('Error', 'An error occurred while deleting the table.');
            }
          } else {
            showError('Error', 'Table not found. It may have already been deleted.');
          }
        } else {
          showError('Error', 'Failed to reload tables. Please try again.');
        }
      } catch (err) {
        showError('Error', 'An error occurred while reloading tables.');
      }
      return;
    }

    try {
      const response = await apiService.deleteTable(table._id);
      if (response.status) {
        // Clear localStorage to force fresh fetch on next load
        localStorage.removeItem('tables');
        setTables(tables.filter((t: QRTable) => t._id !== table._id));
        showSuccess('Table deleted successfully.');
      } else {
        showError('Failed to delete table', response.message || 'Unable to delete table.');
      }
    } catch (err) {
      showError('Error', 'An error occurred while deleting the table.');
    }
  };

  const handleRemoveSession = (session: TableSession) => {
    setSelectedSession(session);
    setIsRemoveSessionModalOpen(true);
  };

  const confirmRemoveSession = async () => {
    if (!selectedSession) return;

    setIsRemovingSession(true);
    try {
      const response = await apiService.updateSessionStatus(selectedSession._id, 'REMOVED');
      if (response && response.status) {
        // Clear localStorage to force fresh fetch on next load (session removal affects table data)
        localStorage.removeItem('tables');
        // Show success toast
        showSuccess('User Removed Successfully', 'The user has been removed and the table is now available for new customers.');
        // Refresh the table list
        await fetchTableSessions();
        setIsRemoveSessionModalOpen(false);
        setSelectedSession(null);
      } else {
        // Show error toast only
        showError('Failed to Remove User', response?.message || 'Failed to remove session');
      }
    } catch (err) {
      // Show error toast only
      showError('Failed to Remove User', 'An error occurred while removing the user');
    } finally {
      setIsRemovingSession(false);
    }
  };

  const generateQRCode = async (table: QRTable) => {
    setIsGeneratingQR(true);
    try {
      const restaurantDetails = getRestaurantDetails();
      const tableUrl = buildRestaurantTableUrl(restaurantDetails || undefined, table.name);
      
      // Generate QR code as data URL
      const qrDataURL = await generateTableQRDataURL(tableUrl);
      
      setQrCodeDataURL(qrDataURL);
      setSelectedTableForQR(table);
      window.history.pushState({ modalOpen: true }, '');
      setIsQRModalOpen(true);
    } catch (err) {
      showError('QR Code', 'Failed to generate QR code');
    } finally {
      setIsGeneratingQR(false);
    }
  };

  const downloadQRCodeImage = () => {
    if (!qrCodeDataURL || !selectedTableForQR) return;
    
    const link = document.createElement('a');
    link.download = `QR_Code_${selectedTableForQR.name}.png`;
    link.href = qrCodeDataURL;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const printMenuQRCode = (scanText: string) => {
    if (!qrCodeDataURL || !selectedTableForQR) return;

    const instructionsText = escapePrintHtml(normalizeScanPrintText(scanText));
    const restaurantDetails = getRestaurantDetails();
    const tableUrl = buildRestaurantTableUrl(restaurantDetails || undefined, selectedTableForQR.name);
    
    // Get theme colors
    const theme = (restaurantDetails as any)?.theme;
    const primaryColor = theme?.primary || '#f97316';
    const textSecondaryColor = theme?.textSecondary || '#4b5563';
    
    const { cardWidthMm, cardHeightMm, pagePaddingMm, qrBoxPx } = MENU_QR_CARD_LAYOUT;
    
    // Detect mobile device
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // Generate HTML content
    const printHTML = `
        <!DOCTYPE html>
        <html lang="en">
          <head>
            <meta charset="UTF-8">
            <title>QR Code - Table ${selectedTableForQR.name}</title>
            <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;600&display=swap" rel="stylesheet">
            <style>
              @page {
                size: A4;
                margin: 0;
              }
              * {
                box-sizing: border-box;
                margin: 0;
                padding: 0;
              }
              body { 
                font-family: 'Inter', sans-serif;
                margin: 0;
                padding: 0;
                background: #ffffff;
              }
              .page {
                width: 210mm;
                height: 297mm;
                background: #ffffff;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: ${pagePaddingMm}mm;
                box-sizing: border-box;
              }
              .card {
                width: ${cardWidthMm}mm;
                height: ${cardHeightMm}mm;
                background: #ffffff;
                border-radius: 16px;
                padding: 6mm;
                box-shadow: 0 8px 24px rgba(0,0,0,0.12);
                box-sizing: border-box;
                flex-shrink: 0;
              }
              .card-inner {
                width: 100%;
                height: 100%;
                border-radius: 12px;
                overflow: hidden;
                border: 2px solid ${primaryColor};
                display: flex;
                flex-direction: column;
                box-shadow: inset 0 0 0 1px rgba(0,0,0,0.05);
                box-sizing: border-box;
              }
              .header {
                background: ${primaryColor};
                color: #ffffff;
                padding: 20px 16px;
                text-align: center;
                flex-shrink: 0;
                box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                min-height: 80px;
                display: flex;
                flex-direction: column;
                justify-content: center;
              }
              .restaurant-name {
                font-size: 22px;
                font-weight: 700;
                margin-bottom: 8px;
                letter-spacing: -0.3px;
                line-height: 1.2;
                text-transform: uppercase;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }
              .table-number {
                font-size: 13px;
                opacity: 0.92;
                font-weight: 400;
                letter-spacing: 0.2px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }
              .body {
                flex: 1;
                padding: 24px 20px 20px;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: space-between;
                position: relative;
                background: #ffffff;
                min-height: 0;
                box-sizing: border-box;
              }
              .qr-container {
                flex: 1;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                width: 100%;
                min-height: 0;
              }
              .qr-code {
                width: ${qrBoxPx}px;
                height: ${qrBoxPx}px;
                max-width: ${qrBoxPx}px;
                max-height: ${qrBoxPx}px;
                margin: 0 auto 16px;
                flex-shrink: 0;
                padding: 12px;
                background: #ffffff;
                border-radius: 8px;
                box-shadow: 0 2px 8px rgba(0,0,0,0.08);
                box-sizing: border-box;
              }
              .qr-code img {
                width: 100%;
                height: 100%;
                display: block;
                border-radius: 4px;
                object-fit: contain;
              }
              .instructions {
                font-size: 11px;
                color: ${textSecondaryColor};
                text-align: center;
                letter-spacing: 0.2px;
                font-weight: 500;
                margin-bottom: 12px;
                line-height: 1.4;
                flex-shrink: 0;
              }
              .url-footer {
                width: 100%;
                text-align: center;
                font-size: 9px;
                color: ${textSecondaryColor};
                font-weight: 400;
                padding: 10px 8px;
                background: #f9fafb;
                border-radius: 6px;
                border: 1px solid #e5e7eb;
                word-break: break-all;
                line-height: 1.3;
                flex-shrink: 0;
                box-sizing: border-box;
              }
              @media print {
                @page {
                  size: A4;
                  margin: 0;
                  padding: 0;
                }
                * {
                  -webkit-print-color-adjust: exact !important;
                  print-color-adjust: exact !important;
                  color-adjust: exact !important;
                }
                html, body {
                  width: 210mm !important;
                  height: 297mm !important;
                  margin: 0 !important;
                  padding: 0 !important;
                  background: #ffffff !important;
                  overflow: hidden !important;
                }
                .page {
                  margin: 0 !important;
                  padding: ${pagePaddingMm}mm !important;
                  width: 210mm !important;
                  height: 297mm !important;
                  display: flex !important;
                  align-items: center !important;
                  justify-content: center !important;
                  box-sizing: border-box !important;
                }
                .card {
                  box-shadow: none !important;
                  padding: 6mm !important;
                  border-radius: 16px !important;
                  width: ${cardWidthMm}mm !important;
                  height: ${cardHeightMm}mm !important;
                  max-width: 100% !important;
                  max-height: 100% !important;
                  margin: 0 !important;
                }
                .card-inner {
                  border-radius: 12px !important;
                  border: 2px solid ${primaryColor} !important;
                  width: 100% !important;
                  height: 100% !important;
                  box-sizing: border-box !important;
                }
                .header {
                  background: ${primaryColor} !important;
                  color: #ffffff !important;
                  -webkit-print-color-adjust: exact !important;
                  print-color-adjust: exact !important;
                  padding: 20px 16px !important;
                }
                .restaurant-name {
                  color: #ffffff !important;
                  -webkit-print-color-adjust: exact !important;
                  print-color-adjust: exact !important;
                }
                .table-number {
                  color: rgba(255, 255, 255, 0.92) !important;
                  -webkit-print-color-adjust: exact !important;
                  print-color-adjust: exact !important;
                }
                .body {
                  background: #ffffff !important;
                  padding: 24px 20px 20px !important;
                }
                .qr-code {
                  background: #ffffff !important;
                  box-shadow: 0 2px 8px rgba(0,0,0,0.08) !important;
                  -webkit-print-color-adjust: exact !important;
                  print-color-adjust: exact !important;
                }
                .instructions {
                  color: ${textSecondaryColor} !important;
                  -webkit-print-color-adjust: exact !important;
                  print-color-adjust: exact !important;
                }
                .url-footer {
                  background: #f9fafb !important;
                  border: 1px solid #e5e7eb !important;
                  color: ${textSecondaryColor} !important;
                  -webkit-print-color-adjust: exact !important;
                  print-color-adjust: exact !important;
                }
                .qr-code img {
                  -webkit-print-color-adjust: exact !important;
                  print-color-adjust: exact !important;
                }
              }
              @media screen {
                body {
                  background: #f0f0f0;
                  padding: 20px;
                }
                .page {
                  margin-bottom: 20px;
                }
              }
            </style>
          </head>
          <body>
            <div class="page">
              <div class="card">
                <div class="card-inner">
                  <div class="header">
                    <div class="restaurant-name">${restaurantDetails?.name || 'Restaurant'}</div>
                    <div class="table-number">${selectedTableForQR.name} | MENU</div>
                  </div>
                  <div class="body">
                    <div class="qr-container">
                      <div class="qr-code">
                        <img src="${qrCodeDataURL}" alt="QR Code for Table ${selectedTableForQR.name}" />
                      </div>
                      <div class="instructions">
                        ${instructionsText}
                      </div>
                    </div>
                    <div class="url-footer">
                      ${tableUrl}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </body>
        </html>
      `;
    
    if (isMobile) {
      // For mobile: use iframe approach which works better with mobile browsers
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      document.body.appendChild(iframe);
      
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      if (iframeDoc) {
        iframeDoc.open();
        iframeDoc.write(printHTML);
        iframeDoc.close();
        
        // Wait for content to load
        iframe.onload = () => {
          setTimeout(() => {
            if (iframe.contentWindow) {
              iframe.contentWindow.focus();
              iframe.contentWindow.print();
            }
            // Remove iframe after printing
            setTimeout(() => {
              if (iframe.parentNode) {
                document.body.removeChild(iframe);
              }
            }, 1000);
          }, 500);
        };
        
        // Fallback: trigger print even if onload doesn't fire
        setTimeout(() => {
          if (iframe.contentWindow) {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
          }
          setTimeout(() => {
            if (iframe.parentNode) {
              document.body.removeChild(iframe);
            }
          }, 1000);
        }, 1000);
      }
    } else {
      // For desktop: use popup window
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(printHTML);
        printWindow.document.close();
        
        // Wait for images to load before printing
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            // Close window after print dialog is dismissed
            printWindow.onafterprint = () => {
              printWindow.close();
            };
            // Fallback: close after a delay if afterprint doesn't fire
            setTimeout(() => {
              if (!printWindow.closed) {
                printWindow.close();
              }
            }, 1000);
          }, 250);
        };
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  const toggleTableSelection = (tableId: string) => {
    setSelectedTableIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tableId)) {
        newSet.delete(tableId);
      } else {
        newSet.add(tableId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedTableIds.size === tables.length) {
      setSelectedTableIds(new Set());
    } else {
      setSelectedTableIds(new Set(tables.map(t => t._id || '')));
    }
  };

  const printMultipleTables = async (scanText: string) => {
    if (selectedTableIds.size === 0) return;

    const instructionsText = escapePrintHtml(normalizeScanPrintText(scanText));
    
    setIsPrintingMultiple(true);
    try {
      const restaurantDetails = getRestaurantDetails();
      const theme = (restaurantDetails as any)?.theme;
      const primaryColor = theme?.primary || '#f97316';
      const textSecondaryColor = theme?.textSecondary || '#4b5563';
      
      // Get selected tables
      const selectedTables = tables.filter(t => selectedTableIds.has(t._id || ''));
      
      // Generate QR codes for all selected tables
      const tableQRData = await Promise.all(
        selectedTables.map(async (table) => {
          const tableUrl = buildRestaurantTableUrl(restaurantDetails || undefined, table.name);
          const qrDataURL = await generateTableQRDataURL(tableUrl);
          return {
            table,
            qrDataURL,
            tableUrl
          };
        })
      );
      
      const { cardWidthMm, cardHeightMm, gridCols, gridRows, itemsPerPage, pagePaddingMm, qrBoxPx } =
        MENU_QR_CARD_LAYOUT;

      const pages = [];
      for (let i = 0; i < tableQRData.length; i += itemsPerPage) {
        pages.push(tableQRData.slice(i, i + itemsPerPage));
      }
      
      // Detect mobile device
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      // Generate HTML for all pages
      const pagesHTML = pages.map((pageData) => {
          const cardsHTML = pageData.map(({ table, qrDataURL, tableUrl }) => `
            <div class="card">
              <div class="card-inner">
                <div class="header">
                  <div class="restaurant-name">${restaurantDetails?.name || 'Restaurant'}</div>
                  <div class="table-number">${table.name} | MENU</div>
                </div>
                <div class="body">
                  <div class="qr-container">
                    <div class="qr-code">
                      <img src="${qrDataURL}" alt="QR Code for Table ${table.name}" />
                    </div>
                    <div class="instructions">
                      ${instructionsText}
                    </div>
                  </div>
                  <div class="url-footer">
                    🔗 ${tableUrl}
                  </div>
                </div>
              </div>
            </div>
          `).join('');
          
          return `
            <div class="page">
              ${cardsHTML}
            </div>
          `;
        }).join('');
        
        // Generate full HTML
        const printHTML = `
          <!DOCTYPE html>
          <html lang="en">
            <head>
              <meta charset="UTF-8">
              <title>QR Codes - ${tableQRData.length} Tables</title>
              <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;600&display=swap" rel="stylesheet">
              <style>
                @page {
                  size: A4;
                  margin: 0;
                }
                * {
                  box-sizing: border-box;
                  margin: 0;
                  padding: 0;
                }
                body { 
                  font-family: 'Inter', sans-serif;
                  margin: 0;
                  padding: 0;
                  background: #ffffff;
                }
                .page {
                  width: 210mm;
                  height: 297mm;
                  background: #ffffff;
                  display: grid;
                  grid-template-columns: repeat(${gridCols}, ${cardWidthMm}mm);
                  grid-template-rows: ${cardHeightMm}mm;
                  gap: 4mm;
                  justify-content: center;
                  align-content: center;
                  padding: ${pagePaddingMm}mm;
                  box-sizing: border-box;
                  page-break-after: always;
                  break-after: page;
                  page-break-inside: avoid;
                  break-inside: avoid;
                }
                .page:last-child {
                  page-break-after: auto;
                  break-after: auto;
                }
                .card {
                  width: ${cardWidthMm}mm;
                  height: ${cardHeightMm}mm;
                  background: #ffffff;
                  border-radius: 16px;
                  padding: 6mm;
                  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
                  box-sizing: border-box;
                }
                .card-inner {
                  width: 100%;
                  height: 100%;
                  border-radius: 12px;
                  overflow: hidden;
                  border: 2px solid ${primaryColor};
                  display: flex;
                  flex-direction: column;
                  box-shadow: inset 0 0 0 1px rgba(0,0,0,0.05);
                  box-sizing: border-box;
                }
                .header {
                  background: ${primaryColor};
                  color: #ffffff;
                  padding: 20px 16px;
                  text-align: center;
                  flex-shrink: 0;
                  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                  min-height: 80px;
                  display: flex;
                  flex-direction: column;
                  justify-content: center;
                }
                .restaurant-name {
                  font-size: 22px;
                  font-weight: 700;
                  margin-bottom: 8px;
                  letter-spacing: -0.3px;
                  line-height: 1.2;
                  text-transform: uppercase;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                }
                .table-number {
                  font-size: 13px;
                  opacity: 0.92;
                  font-weight: 400;
                  letter-spacing: 0.2px;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                }
                .body {
                  flex: 1;
                  padding: 24px 20px 20px;
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: space-between;
                  position: relative;
                  background: #ffffff;
                  min-height: 0;
                  box-sizing: border-box;
                }
                .qr-container {
                  flex: 1;
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                  width: 100%;
                  min-height: 0;
                }
                .qr-code {
                  width: ${qrBoxPx}px;
                  height: ${qrBoxPx}px;
                  max-width: ${qrBoxPx}px;
                  max-height: ${qrBoxPx}px;
                  margin: 0 auto 16px;
                  flex-shrink: 0;
                  padding: 12px;
                  background: #ffffff;
                  border-radius: 8px;
                  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
                  box-sizing: border-box;
                }
                .qr-code img {
                  width: 100%;
                  height: 100%;
                  display: block;
                  border-radius: 4px;
                  object-fit: contain;
                }
                .instructions {
                  font-size: 11px;
                  color: ${textSecondaryColor};
                  text-align: center;
                  letter-spacing: 0.2px;
                  font-weight: 500;
                  margin-bottom: 12px;
                  line-height: 1.4;
                  flex-shrink: 0;
                }
                .url-footer {
                  width: 100%;
                  text-align: center;
                  font-size: 9px;
                  color: ${textSecondaryColor};
                  font-weight: 400;
                  padding: 10px 8px;
                  background: #f9fafb;
                  border-radius: 6px;
                  border: 1px solid #e5e7eb;
                  word-break: break-all;
                  line-height: 1.3;
                  flex-shrink: 0;
                  box-sizing: border-box;
                }
                @media print {
                  @page {
                    size: A4;
                    margin: 0;
                    padding: 0;
                  }
                  * {
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                    color-adjust: exact !important;
                  }
                  html, body {
                    width: 210mm !important;
                    height: auto !important;
                    min-height: 297mm !important;
                    margin: 0 !important;
                    padding: 0 !important;
                    background: #ffffff !important;
                    overflow: visible !important;
                  }
                  .page {
                    margin: 0 !important;
                    padding: ${pagePaddingMm}mm !important;
                    width: 210mm !important;
                    height: 297mm !important;
                    display: grid !important;
                    grid-template-columns: repeat(${gridCols}, ${cardWidthMm}mm) !important;
                    grid-template-rows: ${cardHeightMm}mm !important;
                    gap: 4mm !important;
                    justify-content: center !important;
                    align-content: center !important;
                    box-sizing: border-box !important;
                    page-break-after: always !important;
                    break-after: page !important;
                    page-break-inside: avoid !important;
                    break-inside: avoid !important;
                  }
                  .page:last-child {
                    page-break-after: auto !important;
                    break-after: auto !important;
                  }
                  .card {
                    box-shadow: none !important;
                    padding: 6mm !important;
                    border-radius: 16px !important;
                    width: ${cardWidthMm}mm !important;
                    height: ${cardHeightMm}mm !important;
                    max-width: 100% !important;
                    max-height: 100% !important;
                    margin: 0 !important;
                  }
                  .card-inner {
                    border-radius: 12px !important;
                    border: 2px solid ${primaryColor} !important;
                    width: 100% !important;
                    height: 100% !important;
                    box-sizing: border-box !important;
                  }
                  .header {
                    background: ${primaryColor} !important;
                    color: #ffffff !important;
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                    padding: 20px 16px !important;
                  }
                  .restaurant-name {
                    color: #ffffff !important;
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                  }
                  .table-number {
                    color: rgba(255, 255, 255, 0.92) !important;
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                  }
                  .body {
                    background: #ffffff !important;
                    padding: 24px 20px 20px !important;
                  }
                  .qr-code {
                    background: #ffffff !important;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.08) !important;
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                  }
                  .instructions {
                    color: ${textSecondaryColor} !important;
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                  }
                  .url-footer {
                    background: #f9fafb !important;
                    border: 1px solid #e5e7eb !important;
                    color: ${textSecondaryColor} !important;
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                  }
                  .qr-code img {
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                  }
                }
                @media screen {
                  body {
                    background: #f0f0f0;
                    padding: 20px;
                  }
                  .page {
                    margin-bottom: 20px;
                  }
                }
              </style>
            </head>
            <body>
              ${pagesHTML}
            </body>
          </html>
        `;
        
        if (isMobile) {
          // For mobile: use iframe approach which works better with mobile browsers
          const iframe = document.createElement('iframe');
          iframe.style.position = 'fixed';
          iframe.style.right = '0';
          iframe.style.bottom = '0';
          iframe.style.width = '0';
          iframe.style.height = '0';
          iframe.style.border = '0';
          document.body.appendChild(iframe);
          
          const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
          if (iframeDoc) {
            iframeDoc.open();
            iframeDoc.write(printHTML);
            iframeDoc.close();
            
            // Wait for content to load
            iframe.onload = () => {
              setTimeout(() => {
                if (iframe.contentWindow) {
                  iframe.contentWindow.focus();
                  iframe.contentWindow.print();
                }
                // Remove iframe after printing
                setTimeout(() => {
                  if (iframe.parentNode) {
                    document.body.removeChild(iframe);
                  }
                  setIsPrintingMultiple(false);
                }, 1000);
              }, 500);
            };
            
            // Fallback: trigger print even if onload doesn't fire
            setTimeout(() => {
              if (iframe.contentWindow) {
                iframe.contentWindow.focus();
                iframe.contentWindow.print();
              }
              setTimeout(() => {
                if (iframe.parentNode) {
                  document.body.removeChild(iframe);
                }
                setIsPrintingMultiple(false);
              }, 1000);
            }, 1000);
          }
        } else {
          // For desktop: use popup window
          const printWindow = window.open('', '_blank');
          if (printWindow) {
            printWindow.document.write(printHTML);
            printWindow.document.close();
            
            // Wait for all images to load before printing
            let hasPrinted = false; // Flag to prevent multiple print calls
            
            const triggerPrint = () => {
              if (hasPrinted || printWindow.closed) return;
              hasPrinted = true;
              
              // Ensure all pages are rendered
              setTimeout(() => {
                if (!printWindow.closed) {
                  printWindow.print();
                  // Close window after print dialog is dismissed
                  printWindow.onafterprint = () => {
                    if (!printWindow.closed) {
                      printWindow.close();
                    }
                    setIsPrintingMultiple(false);
                  };
                  // Fallback: close after a delay if afterprint doesn't fire
                  setTimeout(() => {
                    if (!printWindow.closed) {
                      printWindow.close();
                    }
                    setIsPrintingMultiple(false);
                  }, 1000);
                }
              }, 500); // Increased delay to ensure all pages render
            };
            
            printWindow.onload = () => {
              // Wait for all images to load
              const images = printWindow.document.querySelectorAll('img');
              let imagesLoaded = 0;
              const totalImages = images.length;
              
              if (totalImages === 0) {
                // No images, proceed immediately
                triggerPrint();
                return;
              }
              
              const checkAllLoaded = () => {
                imagesLoaded++;
                if (imagesLoaded === totalImages && !hasPrinted) {
                  // All images loaded, trigger print
                  triggerPrint();
                }
              };
              
              images.forEach((img) => {
                if (img.complete) {
                  checkAllLoaded();
                } else {
                  img.onload = checkAllLoaded;
                  img.onerror = checkAllLoaded; // Continue even if image fails
                }
              });
              
              // Fallback timeout in case images don't load (only if not already printed)
              setTimeout(() => {
                if (!hasPrinted && !printWindow.closed) {
                  triggerPrint();
                }
              }, 3000);
            };
          }
        }
    } catch (err) {
      showError('Print', 'Failed to generate QR codes for printing');
      setIsPrintingMultiple(false);
    }
  };

  const openQRPrintOptions = (mode: QRPrintOptionsMode) => {
    const isSingle = mode.endsWith('-single');
    if (isSingle && (!qrCodeDataURL || !selectedTableForQR)) return;
    if (!isSingle && selectedTableIds.size === 0) return;
    setQrPrintOptionsMode(mode);
    setQrPrintScanTextDraft(readStoredQRScanText());
    setIsQRPrintOptionsOpen(true);
  };

  const closeQRPrintOptions = () => {
    setIsQRPrintOptionsOpen(false);
  };

  const persistQRPrintScanText = (scanText: string) => {
    const normalized = normalizeScanPrintText(scanText);
    try {
      localStorage.setItem(QR_PRINT_SCAN_TEXT_STORAGE_KEY, normalized);
    } catch {
      /* ignore */
    }
    return normalized;
  };

  const executeQRPrintFromOptions = async () => {
    const scanText = persistQRPrintScanText(qrPrintScanTextDraft);

    closeQRPrintOptions();

    if (qrPrintOptionsMode === 'menu-single') {
      printMenuQRCode(scanText);
      return;
    }

    if (qrPrintOptionsMode === 'menu-bulk') {
      await printMultipleTables(scanText);
      return;
    }

    const restaurantDetails = getRestaurantDetails();
    const theme = (restaurantDetails as any)?.theme;
    const textSecondaryColor = theme?.textSecondary || '#4b5563';
    const labelsPerPage = SMALL_QR_LABEL_LAYOUT.labelsPerPage;

    if (qrPrintOptionsMode === 'compact-single') {
      if (!qrCodeDataURL || !selectedTableForQR) return;

      const label: SmallQRPrintLabel = {
        tableName: selectedTableForQR.name,
        qrDataURL: qrCodeDataURL,
      };
      const labels = Array.from({ length: labelsPerPage }, () => label);

      const printHTML = buildCompactQRCodesPrintHTML(labels, {
        title: `Small QR Labels - Table ${selectedTableForQR.name}`,
        textSecondaryColor,
        scanText,
      });

      openQRPrintDocument(printHTML);
      return;
    }

    if (selectedTableIds.size === 0) return;

    setIsPrintingSmallMultiple(true);
    try {
      const selectedTables = tables.filter((t) => selectedTableIds.has(t._id || ''));

      const labels: SmallQRPrintLabel[] = await Promise.all(
        selectedTables.map(async (table) => {
          const tableUrl = buildRestaurantTableUrl(restaurantDetails || undefined, table.name);
          const qrDataURL = await generateTableQRDataURL(tableUrl);
          return { tableName: table.name, qrDataURL };
        })
      );

      const printHTML = buildCompactQRCodesPrintHTML(labels, {
        title: `Small QR Labels - ${labels.length} Tables`,
        textSecondaryColor,
        scanText,
      });

      openQRPrintDocument(printHTML, () => setIsPrintingSmallMultiple(false));
    } catch {
      showError('Print', 'Failed to generate QR labels for printing');
      setIsPrintingSmallMultiple(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 w-full sm:w-auto">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={selectedTableIds.size === tables.length && tables.length > 0}
              onChange={handleSelectAll}
              className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
            />
            <span className="text-sm text-gray-700 whitespace-nowrap">
              Select All ({selectedTableIds.size} selected)
            </span>
          </label>
          {selectedTableIds.size > 0 && (
            <>
              <button
                onClick={() => openQRPrintOptions('menu-bulk')}
                disabled={isPrintingMultiple || isPrintingSmallMultiple}
                className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <PrinterIcon className="h-5 w-5 mr-2" />
                <span className="whitespace-nowrap">
                  {isPrintingMultiple ? 'Printing...' : `Print Selected (${selectedTableIds.size})`}
                </span>
              </button>
              <button
                onClick={() => openQRPrintOptions('compact-bulk')}
                disabled={isPrintingSmallMultiple || isPrintingMultiple}
                className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
              >
                <PrinterIcon className="h-5 w-5 mr-2" />
                <span className="whitespace-nowrap">
                  {isPrintingSmallMultiple
                    ? 'Printing...'
                    : `Print QR Labels (${selectedTableIds.size})`}
                </span>
              </button>
            </>
          )}
        </div>
        <button
          onClick={() => {
            window.history.pushState({ modalOpen: true }, '');
            setIsCreateModalOpen(true);
          }}
          className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Table
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tables.map((table) => (
          <div key={table._id} className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={selectedTableIds.has(table._id || '')}
                  onChange={() => toggleTableSelection(table._id || '')}
                  className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                  onClick={(e) => e.stopPropagation()}
                />
                <h3 className="text-lg font-semibold text-gray-900">{table.name}</h3>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleEditTable(table)}
                  className="text-orange-600 hover:text-orange-900"
                  title="Edit Table"
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => generateQRCode(table)}
                  disabled={isGeneratingQR}
                  className="text-green-600 hover:text-green-900 disabled:opacity-50"
                  title="Generate QR Code"
                >
                  <QrCodeIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => handleDelete(table)}
                  className="text-red-600 hover:text-red-900"
                  title="Delete Table"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {tables.length === 0 && !loading && (
        <div className="text-center py-12">
          <p className="text-gray-500">No tables found. Add your first table to get started.</p>
        </div>
      )}

      {/* Create Table Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 !mt-0">
          <div 
            onClick={() => {
              if (window.history.state?.modalOpen) {
                window.history.back();
              }
              setIsCreateModalOpen(false);
            }} 
            className="absolute inset-0" 
          />
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto relative">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4 sm:mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Create new table</h3>
                <button 
                  onClick={() => {
                    if (window.history.state?.modalOpen) {
                      window.history.back();
                    }
                    setIsCreateModalOpen(false);
                  }} 
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                >
                  ×
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Table Name *
                  </label>
                  <input
                    type="text"
                    value={newTableName}
                    onChange={(e) => setNewTableName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                    placeholder="Enter table name (e.g., A1, B2, etc.)"
                    autoFocus
                  />
                </div>
                <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4">
                  <button
                    onClick={() => {
                      if (window.history.state?.modalOpen) {
                        window.history.back();
                      }
                      setIsCreateModalOpen(false);
                    }}
                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateTable}
                    disabled={isCreating || !newTableName.trim()}
                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-md disabled:opacity-50 transition-colors"
                  >
                    {isCreating ? 'Creating...' : 'Create Table'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Table Modal */}
      {isEditModalOpen && editingTable && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 !mt-0">
          <div 
            onClick={() => {
              if (window.history.state?.modalOpen) {
                window.history.back();
              }
              setIsEditModalOpen(false);
            }} 
            className="absolute inset-0" 
          />
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto relative">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4 sm:mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Edit Table</h3>
                <button 
                  onClick={() => {
                    if (window.history.state?.modalOpen) {
                      window.history.back();
                    }
                    setIsEditModalOpen(false);
                  }} 
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                >
                  ×
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Table Name *
                  </label>
                  <input
                    type="text"
                    value={editTableName}
                    onChange={(e) => setEditTableName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                    placeholder="Enter table name"
                    autoFocus
                  />
                </div>
                <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4">
                  <button
                    onClick={() => {
                      if (window.history.state?.modalOpen) {
                        window.history.back();
                      }
                      setIsEditModalOpen(false);
                    }}
                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateTable}
                    disabled={isUpdating || !editTableName.trim()}
                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-md disabled:opacity-50 transition-colors"
                  >
                    {isUpdating ? 'Updating...' : 'Update Table'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Remove User Confirmation Modal */}
      {isRemoveSessionModalOpen && selectedSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 !mt-0">
          <div onClick={() => setIsRemoveSessionModalOpen(false)} className="absolute inset-0" />
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto relative">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4 sm:mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Remove User & Clear Table</h3>
                <button 
                  onClick={() => setIsRemoveSessionModalOpen(false)} 
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                >
                  ×
                </button>
              </div>
              <div className="space-y-4">
                <div className="text-center">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                    <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-medium text-gray-900 mb-2">Confirm User Removal</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Are you sure you want to remove this user and clear the table? This action will end their session and make the table available for new customers.
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h5 className="font-medium text-gray-900 mb-2">User Details:</h5>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">User ID:</span>
                      <span className="font-mono text-xs text-gray-900">{selectedSession._id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status:</span>
                      <span className="font-medium text-gray-900 capitalize">{selectedSession.status}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Joined:</span>
                      <span className="text-gray-900">{formatDate(selectedSession.createdAt)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                  <button
                    onClick={() => setIsRemoveSessionModalOpen(false)}
                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmRemoveSession}
                    disabled={isRemovingSession}
                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50 transition-colors"
                  >
                    {isRemovingSession ? 'Removing User...' : 'Remove User'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {isQRModalOpen && selectedTableForQR && qrCodeDataURL && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 !mt-0">
          <div 
            onClick={() => {
              if (window.history.state?.modalOpen) {
                window.history.back();
              }
              setIsQRModalOpen(false);
            }} 
            className="absolute inset-0" 
          />
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto relative">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4 sm:mb-6">
                <h3 className="text-lg font-semibold text-gray-900">QR Code - Table {selectedTableForQR.name}</h3>
                <button 
                  onClick={() => {
                    if (window.history.state?.modalOpen) {
                      window.history.back();
                    }
                    setIsQRModalOpen(false);
                  }} 
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                >
                  ×
                </button>
              </div>
              <div className="space-y-4">
                <div className="text-center">
                  <div className="mb-4">
                    <img 
                      src={qrCodeDataURL} 
                      alt={`QR Code for Table ${selectedTableForQR.name}`}
                      className="mx-auto border border-gray-200 rounded-lg max-w-full h-auto"
                    />
                  </div>
                  {(() => {
                    const restaurantDetails = getRestaurantDetails();
                    return restaurantDetails?.name ? (
                      <div className="text-lg font-bold text-gray-900 mb-2">
                        {restaurantDetails.name}
                      </div>
                    ) : null;
                  })()}
                  <div className="text-sm text-gray-600 mb-2">
                    <strong>Table:</strong> {selectedTableForQR.name}
                  </div>
                  <div className="text-xs text-gray-500 break-all">
                    <strong>URL:</strong> {(() => {
                      const restaurantDetails = getRestaurantDetails();
                      return buildRestaurantTableUrl(restaurantDetails || undefined, selectedTableForQR.name);
                    })()}
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-2">
                  <button
                    onClick={downloadQRCodeImage}
                    className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-md transition-colors"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                    Download
                  </button>
                  <button
                    onClick={() => openQRPrintOptions('menu-single')}
                    className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
                  >
                    <PrinterIcon className="h-4 w-4 mr-2" />
                    Print
                  </button>
                  <button
                    onClick={() => openQRPrintOptions('compact-single')}
                    className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors"
                  >
                    <PrinterIcon className="h-4 w-4 mr-2" />
                    Print QR label
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QR label print options */}
      {isQRPrintOptionsOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 !mt-0">
          <div onClick={closeQRPrintOptions} className="absolute inset-0" aria-hidden />
          <div className="relative w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {qrPrintOptionsMode.startsWith('menu') ? 'Print menu QR' : 'Print QR labels'}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {qrPrintOptionsMode.endsWith('-single')
                    ? `Table ${selectedTableForQR?.name || ''}`
                    : `${selectedTableIds.size} table(s) selected`}
                </p>
              </div>
              <button
                type="button"
                onClick={closeQRPrintOptions}
                className="text-2xl leading-none text-gray-400 hover:text-gray-600"
                aria-label="Close print options"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label htmlFor="qr-print-scan-text-draft" className="mb-1.5 block text-sm font-medium text-gray-700">
                  Scan text
                </label>
                <input
                  id="qr-print-scan-text-draft"
                  type="text"
                  value={qrPrintScanTextDraft}
                  onChange={(e) => setQrPrintScanTextDraft(e.target.value)}
                  placeholder={DEFAULT_QR_SCAN_TEXT}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/30"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Default: {DEFAULT_QR_SCAN_TEXT}
                  {qrPrintOptionsMode.startsWith('menu')
                    ? ' · 6×4 in (10.16 × 15.24 cm), 2 cards per A4 row'
                    : ' · Small labels 3 × 2.75 in (8 per A4 sheet)'}
                </p>
              </div>
            </div>

            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={closeQRPrintOptions}
                className="inline-flex justify-center rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void executeQRPrintFromOptions()}
                disabled={isPrintingSmallMultiple || isPrintingMultiple}
                className="inline-flex justify-center rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {isPrintingSmallMultiple || isPrintingMultiple ? 'Preparing…' : 'Print'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Customer Orders Modal */}
      {isCustomerModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 !mt-0">
          <div 
            onClick={() => {
              if (window.history.state?.modalOpen) {
                window.history.back();
              }
              setIsCustomerModalOpen(false);
            }} 
            className="absolute inset-0" 
          />
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto relative">
            <div className="p-4 sm:p-6">
              <div className="flex justify-end items-center mb-4 sm:mb-6">
                <button 
                  onClick={() => {
                    if (window.history.state?.modalOpen) {
                      window.history.back();
                    }
                    setIsCustomerModalOpen(false);
                  }} 
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                >
                  ×
                </button>
              </div>
              
              <div id="customer-orders-print">
                {customerOrdersLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
                  </div>
                ) : customerOrders.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No orders found for this customer.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {customerOrders.map((order, index) => (
                      <div key={order._id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                              Order #{index + 1} - {order.invoiceNo}
                            </h3>
                            <p className="text-sm text-gray-600">{formatDate(order.createdAt)}</p>
                            {order.table && (
                              <p className="text-sm text-gray-600">Table: {order.table}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-green-600">₹{order.totalAmount}</p>
                            {order.status && (
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                order.status === 'New' ? 'bg-yellow-100 text-yellow-800' :
                                order.status === 'Confirmed' ? 'bg-orange-100 text-orange-800' :
                                order.status === 'Preparing' ? 'bg-purple-100 text-purple-800' :
                                order.status === 'Ready' ? 'bg-indigo-100 text-indigo-800' :
                                order.status === 'Delivered' ? 'bg-green-100 text-green-800' :
                                order.status === 'Cancelled' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {order.status}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="bg-white p-3 rounded border">
                          <h4 className="font-semibold text-gray-900 mb-2">Items Ordered:</h4>
                          <div className="space-y-1">
                            {order.products.map((product, productIndex) => (
                              <div key={productIndex} className="flex justify-between items-center text-sm">
                                <span className="font-medium">{product.name}</span>
                                <span className="text-gray-600">
                                  Qty: {product.quantity} × ₹{product.offerPrice} = ₹{product.offerPrice * product.quantity}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    <div className="bg-gray-900 text-white p-4 rounded-lg text-center">
                      <h3 className="text-xl font-bold">
                        Grand Total: ₹{calculateTotalAmount(customerOrders)}
                      </h3>
                    </div>
                  </div>
                )}
              </div>
              
              {!customerOrdersLoading && customerOrders.length > 0 && (
                <div className="flex justify-end mt-6 space-x-2">
                  <button
                    onClick={handleDownload}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-700 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                    Download Bill
                  </button>
                  <button
                    onClick={handlePrint}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-600"
                  >
                    <PrinterIcon className="h-4 w-4 mr-2" />
                    Print Bill
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRTableList;
